// La Loge, rendus KeyShotXR (vues à 360°) : règles pures partagées par World (reconnaissance de l'export au dépôt),
// le serveur (métadonnée `loge_files.xr`), et la visionneuse du client (src/loge/viewerXr.ts). Aucune dépendance au
// navigateur : tout se teste sous Node (tests/unit/logeXr.test.ts).
//
// Format d'un export KeyShotXR (KeyShot, « KeyShotXR », sortie « Web ») : une page `<nom>.html` qui charge
// `<folderName>/files/KeyShotXR.js` et appelle `new keyshotXR(nameOfDiv, folderName, viewPortWidth, viewPortHeight,
// backgroundColor, uCount, vCount, uWrap, vWrap, uMouseSensitivity, vMouseSensitivity, uStartIndex, vStartIndex, …,
// imageExtension, …, uReverse, vReverse, …)`, chaque paramètre posé dans une variable du même nom ; à côté, le
// dossier `<folderName>/` contient les vues, nommées `<v>_<u>.<extension>` (rangée verticale v de 0 à vCount − 1,
// vue horizontale u de 0 à uCount − 1), et le sous-dossier `files/` (KeyShotXR.js, icône de chargement).
// Sources : manuel KeyShot, « KeyShotXR Variables » ; exports publiés (page, dossier, `0_0.jpg` … `5_17.jpg`).

/** Vues d'un rendu 360° au plus (pages d'aperçu côté serveur). */
export const MAX_XR_FRAMES = 400;
/** Plus grand côté d'une vue envoyée au client (les rendus fixes gardent PREVIEW_MAX_PX). */
export const XR_PREVIEW_MAX_PX = 2000;
/** Extensions d'image qu'un export KeyShotXR peut produire (JPEG ou PNG ; WebP accepté par précaution). */
export const XR_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const;

/**
 * Métadonnée « XR » d'un fichier (colonne `loge_files.xr`, champ `xr` de la vue du projet). Les pages d'aperçu du
 * fichier sont les vues, la vue de départ en page 0 (vignettes, couverture), puis dans l'ordre de KeyShot (rangée par
 * rangée), en boucle : voir `xrPage`.
 */
export interface XrMeta {
  /** Vues horizontales (une rangée), et rangées verticales. */
  uCount: number;
  vCount: number;
  /** Le tour horizontal (vertical) boucle sur lui-même ; sinon il s'arrête aux extrémités. */
  uWrap: boolean;
  vWrap: boolean;
  /** Vue de départ choisie dans KeyShot. */
  uStart: number;
  vStart: number;
  /** Sens : +1 si un glissement vers la droite (vers le bas) fait avancer l'indice de la vue, −1 sinon. */
  uDir: 1 | -1;
  vDir: 1 | -1;
  /** Fond de l'export (#rrggbb) : autour des vues, et sous les PNG détourés. */
  background: string | null;
}

/** Paramètres lus dans la page d'un export KeyShotXR. */
export interface KeyShotXrParams extends XrMeta {
  folderName: string;
  imageExtension: string;
  viewPortWidth: number | null;
  viewPortHeight: number | null;
}

export const xrFrameCount = (m: Pick<XrMeta, 'uCount' | 'vCount'>): number => m.uCount * m.vCount;

// ── Lecture de la page HTML ──────────────────────────────────────────────────────────────────────────

/** Ordre des arguments de `new keyshotXR(…)` dans les exports de KeyShot (les suivants sont ignorés). */
export const KEYSHOTXR_ARGS = [
  'nameOfDiv', 'folderName', 'viewPortWidth', 'viewPortHeight', 'backgroundColor', 'uCount', 'vCount', 'uWrap', 'vWrap',
  'uMouseSensitivity', 'vMouseSensitivity', 'uStartIndex', 'vStartIndex', 'minZoom', 'maxZoom', 'rotationDamping',
  'downScaleToBrowser', 'addDownScaleGUIButton', 'downloadOnInteraction', 'imageExtension', 'showLoading', 'loadingIcon',
  'allowFullscreen', 'uReverse', 'vReverse', 'hotspots', 'isIBooksWidget',
] as const;

type Lit = string | number | boolean;

/** Valeur littérale JavaScript simple (chaîne, nombre, booléen), sinon undefined. */
function literal(raw: string): Lit | undefined {
  const s = raw.trim();
  if (/^"(?:[^"\\]|\\.)*"$/.test(s) || /^'(?:[^'\\]|\\.)*'$/.test(s)) return s.slice(1, -1).replace(/\\(.)/g, '$1');
  if (/^[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?$/i.test(s)) return Number(s);
  if (s === 'true') return true;
  if (s === 'false') return false;
  return undefined;
}

/** Arguments de premier niveau d'un appel, séparés par les virgules hors chaînes, parenthèses, crochets, accolades. */
function splitArgs(src: string): string[] | null {
  const out: string[] = [];
  let depth = 0, quote = '', cur = '';
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quote) {
      cur += c;
      if (c === '\\') { cur += src[++i] ?? ''; continue; }
      if (c === quote) quote = '';
      continue;
    }
    if (c === '"' || c === "'" || c === '`') { quote = c; cur += c; continue; }
    if (c === '(' || c === '[' || c === '{') depth++;
    if (c === ')' || c === ']' || c === '}') {
      if (depth === 0) { out.push(cur); return out; }
      depth--;
    }
    if (c === ',' && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += c;
  }
  return null;
}

const int = (v: Lit | undefined): number | null => (typeof v === 'number' && Number.isInteger(v) ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : null);
const bool = (v: Lit | undefined, fallback: boolean): boolean => (typeof v === 'boolean' ? v : v === 'true' ? true : v === 'false' ? false : fallback);
const sign = (v: Lit | undefined, fallback: 1 | -1): 1 | -1 => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return !Number.isFinite(n) || n === 0 ? fallback : n < 0 ? -1 : 1;
};
const clampInt = (n: number | null, max: number): number => (n === null ? 0 : Math.min(Math.max(0, n), Math.max(0, max)));

/** Couleur #rrggbb (ou #rgb), en minuscules ; null sinon. */
export function cleanColor(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(s)) return s;
  if (/^#[0-9a-f]{3}$/.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return null;
}

/**
 * Paramètres d'une page KeyShotXR : variables `var nom = valeur;` et arguments de `new keyshotXR(…)` (variables ou
 * valeurs écrites en clair, si la page a été retouchée). Null si ce n'est pas une page KeyShotXR exploitable.
 */
export function parseKeyShotXrHtml(html: string): KeyShotXrParams | null {
  const call = /new\s+keyshotXR\s*\(/i.exec(html);
  if (!call) return null;
  const vars = new Map<string, Lit>();
  const decl = /(?:^|[\s;{(,])(?:var|let|const)?\s*([A-Za-z_$][\w$]*)\s*=\s*("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*'|[-+]?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?|true|false)\s*(?=[;,\n\r}]|\/\/)/gi;
  for (const m of html.matchAll(decl)) {
    const value = literal(m[2]!);
    if (value !== undefined && !vars.has(m[1]!)) vars.set(m[1]!, value);
  }
  const args = splitArgs(html.slice(call.index + call[0].length)) ?? [];
  const value = (name: (typeof KEYSHOTXR_ARGS)[number]): Lit | undefined => {
    const i = KEYSHOTXR_ARGS.indexOf(name);
    const raw = args[i]?.trim();
    if (raw) {
      const lit = literal(raw);
      if (lit !== undefined) return lit;
      if (/^[A-Za-z_$][\w$]*$/.test(raw) && vars.has(raw)) return vars.get(raw);
    }
    return vars.get(name);
  };
  const folderName = value('folderName');
  const uCount = int(value('uCount'));
  const vCount = int(value('vCount'));
  const ext = String(value('imageExtension') ?? '').trim().replace(/^\./, '').toLowerCase();
  if (typeof folderName !== 'string' || !folderName.trim() || /[\\/]|\.\./.test(folderName)) return null;
  if (!uCount || !vCount || uCount < 1 || vCount < 1) return null;
  if (!(XR_EXTENSIONS as readonly string[]).includes(ext)) return null;
  const width = int(value('viewPortWidth'));
  const height = int(value('viewPortHeight'));
  return {
    folderName: folderName.trim(), imageExtension: ext, uCount, vCount,
    uWrap: bool(value('uWrap'), true), vWrap: bool(value('vWrap'), false),
    uStart: clampInt(int(value('uStartIndex')), uCount - 1), vStart: clampInt(int(value('vStartIndex')), vCount - 1),
    // KeyShot avance l'indice dans le sens de (glissement × sensibilité) ; uReverse / vReverse inversent ce sens.
    uDir: (sign(value('uMouseSensitivity'), -1) * (bool(value('uReverse'), false) ? -1 : 1)) as 1 | -1,
    vDir: (sign(value('vMouseSensitivity'), 1) * (bool(value('vReverse'), false) ? -1 : 1)) as 1 | -1,
    background: cleanColor(value('backgroundColor')),
    viewPortWidth: width && width > 0 ? width : null, viewPortHeight: height && height > 0 ? height : null,
  };
}

/**
 * Fichier `<nom>.360` des exports KeyShotXR récents (JSON, à côté des vues) :
 * `{ "type": "KeyShotXR", "horizontalFrames": 18, "verticalFrames": 1, "horizontalStartingIndex": 9, … }`.
 * Il ne dit ni l'extension des vues (déduite des fichiers présents), ni le sens, ni le fond : valeurs par défaut de
 * KeyShot (sensibilité horizontale négative, fond blanc). Null si ce n'est pas un tel fichier.
 */
export function parseKeyShotXr360(text: string, imageExtension: string): KeyShotXrParams | null {
  let o: unknown;
  try { o = JSON.parse(text); } catch { return null; }
  if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
  const j = o as Record<string, unknown>;
  if (typeof j.type !== 'string' || j.type.toLowerCase() !== 'keyshotxr') return null;
  const n = (k: string): number | null => (typeof j[k] === 'number' && Number.isInteger(j[k]) ? (j[k] as number) : null);
  const uCount = n('horizontalFrames'), vCount = n('verticalFrames');
  const ext = imageExtension.replace(/^\./, '').toLowerCase();
  if (!uCount || !vCount || uCount < 1 || vCount < 1 || !(XR_EXTENSIONS as readonly string[]).includes(ext)) return null;
  const name = typeof j.name === 'string' && j.name.trim() && !/[\\/]|\.\./.test(j.name) ? j.name.trim() : 'KeyShotXR';
  return {
    folderName: name, imageExtension: ext, uCount, vCount,
    uWrap: j.horizontalWrap !== false, vWrap: j.verticalWrap === true,
    uStart: clampInt(n('horizontalStartingIndex'), uCount - 1), vStart: clampInt(n('verticalStartingIndex'), vCount - 1),
    uDir: -1, vDir: 1, background: '#ffffff', viewPortWidth: null, viewPortHeight: null,
  };
}

/** Message pour un `.360` déposé seul : il ne contient que la description de l'export, pas les vues. */
export const lone360Message = (name: string): string =>
  `« ${name} » décrit un rendu 360° KeyShotXR, sans ses images. Déposez le dossier de l’export en entier (la page .html, le dossier des vues 0_0.jpg… et ce fichier), ou son ZIP.`;

/** Métadonnée gardée en base, tirée des paramètres de la page. */
export function xrMetaOf(p: KeyShotXrParams): XrMeta {
  return { uCount: p.uCount, vCount: p.vCount, uWrap: p.uWrap, vWrap: p.vWrap, uStart: p.uStart, vStart: p.vStart, uDir: p.uDir, vDir: p.vDir, background: p.background };
}

/**
 * Métadonnée XR reçue (World → serveur, base → client), vérifiée : nombres entiers, au plus MAX_XR_FRAMES vues, vue
 * de départ dans la grille. Null si elle n'est pas exploitable.
 */
export function cleanXrMeta(v: unknown): XrMeta | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const n = (k: string): number | null => (typeof o[k] === 'number' && Number.isInteger(o[k]) ? (o[k] as number) : null);
  const uCount = n('uCount'), vCount = n('vCount'), uStart = n('uStart') ?? 0, vStart = n('vStart') ?? 0;
  if (!uCount || !vCount || uCount < 1 || vCount < 1 || uCount * vCount > MAX_XR_FRAMES) return null;
  if (uStart < 0 || uStart >= uCount || vStart < 0 || vStart >= vCount) return null;
  return {
    uCount, vCount, uWrap: o.uWrap !== false, vWrap: o.vWrap === true, uStart, vStart,
    uDir: o.uDir === 1 ? 1 : -1, vDir: o.vDir === -1 ? -1 : 1,
    background: cleanColor(o.background),
  };
}

/**
 * Envoi annoncé par World (`file/start`) : un fichier ordinaire a `maxPages` aperçus au plus ; un rendu 360° (`xr`) a
 * exactement une page par vue, MAX_XR_FRAMES au plus, et n'est jamais une couverture.
 */
export function checkPreviewPlan(i: { xr: unknown; previewPages: number; cover: boolean; maxPages: number }): { ok: true; xr: XrMeta | null } | { ok: false; message: string } {
  if (!Number.isInteger(i.previewPages) || i.previewPages < 0) return { ok: false, message: 'Nombre d’aperçus invalide.' };
  if (i.xr === undefined || i.xr === null) {
    return i.previewPages <= i.maxPages ? { ok: true, xr: null } : { ok: false, message: `Aperçus : ${i.maxPages} pages au plus.` };
  }
  const xr = cleanXrMeta(i.xr);
  if (!xr) return { ok: false, message: `Rendu 360° illisible (${MAX_XR_FRAMES} vues au plus).` };
  if (i.cover) return { ok: false, message: 'La couverture est une image, pas un rendu 360°.' };
  if (i.previewPages !== xrFrameCount(xr)) return { ok: false, message: `Rendu 360° : ${xrFrameCount(xr)} vues annoncées, ${i.previewPages} aperçus.` };
  return { ok: true, xr };
}

/** Pages d'aperçu qu'un fichier peut recevoir : une par vue pour un rendu 360°, `maxPages` sinon. */
export function previewPageLimit(xr: unknown, maxPages: number): number {
  const m = cleanXrMeta(xr);
  return m ? xrFrameCount(m) : maxPages;
}

// ── Vues et pages d'aperçu ───────────────────────────────────────────────────────────────────────────

/** Nom d'une vue dans le dossier de l'export : `<v>_<u>.<extension>`. */
export const xrFrameName = (u: number, v: number, ext: string): string => `${v}_${u}.${ext}`;

/** Indice de KeyShot d'une vue : rangée par rangée. */
const frameIndex = (m: XrMeta, u: number, v: number): number => v * m.uCount + u;

/** Page d'aperçu d'une vue : la vue de départ en page 0, puis l'ordre de KeyShot, en boucle. */
export function xrPage(m: XrMeta, u: number, v: number): number {
  const n = xrFrameCount(m);
  return (((frameIndex(m, u, v) - frameIndex(m, m.uStart, m.vStart)) % n) + n) % n;
}

/** Vue (u, v) d'une page d'aperçu (inverse de `xrPage`). */
export function xrFrameOfPage(m: XrMeta, page: number): { u: number; v: number } {
  const n = xrFrameCount(m);
  const f = (((page + frameIndex(m, m.uStart, m.vStart)) % n) + n) % n;
  return { u: f % m.uCount, v: Math.floor(f / m.uCount) };
}

/** Vues dans l'ordre des pages d'aperçu (page 0 = vue de départ). */
export function xrFramesInPageOrder(m: XrMeta): Array<{ u: number; v: number }> {
  return Array.from({ length: xrFrameCount(m) }, (_, p) => xrFrameOfPage(m, p));
}

// ── Reconnaissance d'un export (dossier déposé, ou ZIP) ──────────────────────────────────────────────

export interface XrExport {
  params: KeyShotXrParams;
  /** Chemin de la page dans le dépôt. */
  htmlPath: string;
  /** Chemins des vues, dans l'ordre des pages d'aperçu. */
  frames: string[];
  /** Chemins qui font partie de l'export (page, vues, `files/`…) : ce qui part dans l'original. */
  members: string[];
  /** Nom de l'original : le dossier déposé, sinon le nom de l'export (sans extension). */
  name: string;
}

export type XrFindResult = { ok: true; xr: XrExport } | { ok: false; reason: string };

const norm = (p: string): string => p.replace(/\\/g, '/').replace(/^\.?\//, '').replace(/\/{2,}/g, '/');
const dirOf = (p: string): string => (p.includes('/') ? p.slice(0, p.lastIndexOf('/') + 1) : '');
/** Fichiers à ignorer : dossiers système d'un ZIP fait sur Mac, vignettes de Windows. */
export const isJunkPath = (p: string): boolean => /(^|\/)(__MACOSX|\.DS_Store|Thumbs\.db|desktop\.ini)(\/|$)/i.test(p) || /(^|\/)\._/.test(p);
export const isHtmlPath = (p: string): boolean => /\.html?$/i.test(p) && !isJunkPath(p);

/**
 * Cherche un export KeyShotXR parmi des chemins relatifs (dossier déposé, ou contenu d'un ZIP). `readText` lit une page
 * HTML. Null : aucune page KeyShotXR, ce n'est pas un export. `{ ok: false }` : c'en est un, mais inexploitable
 * (vues manquantes, trop de vues) : la raison est à montrer telle quelle.
 */
export async function findXrExport(paths: string[], readText: (path: string) => Promise<string>): Promise<XrFindResult | null> {
  const all = paths.map(norm).filter((p) => p && !isJunkPath(p));
  const byLower = new Map(all.map((p) => [p.toLowerCase(), p]));
  const pages = all.filter(isHtmlPath).sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
  // Description `.360` des exports récents : lue après les pages, si aucune page n'a donné un export complet.
  const manifests = all.filter((p) => /\.360$/i.test(p)).sort((a, b) => a.split('/').length - b.split('/').length || a.localeCompare(b));
  // Extension des vues d'un dossier : celle des fichiers nommés `<v>_<u>.<ext>` qui s'y trouvent.
  const frameExt = (dir: string): string => {
    const hit = all.find((p) => dirOf(p) === dir && /^\d+_\d+\.(jpe?g|png|webp)$/i.test(p.slice(dir.length)));
    return hit ? hit.split('.').pop()!.toLowerCase() : 'jpg';
  };
  let failure: string | null = null;
  const candidates: Array<{ path: string; manifest: boolean }> = [
    ...pages.slice(0, 12).map((path) => ({ path, manifest: false })),
    ...manifests.slice(0, 12).map((path) => ({ path, manifest: true })),
  ];
  for (const { path: htmlPath, manifest } of candidates) {
    let text: string;
    try { text = await readText(htmlPath); } catch { continue; }
    const params = manifest ? parseKeyShotXr360(text, frameExt(dirOf(htmlPath))) : parseKeyShotXrHtml(text);
    if (!params) continue;
    // Un `.360` est rangé avec ses vues : on les cherche d'abord dans son propre dossier.
    if (manifest) params.folderName = '';
    const total = xrFrameCount(params);
    if (total > MAX_XR_FRAMES) {
      failure = `Export KeyShotXR de ${total} vues (${params.uCount} × ${params.vCount}) : ${MAX_XR_FRAMES} au plus. Réexportez avec moins d’images.`;
      continue;
    }
    const meta = xrMetaOf(params);
    const order = xrFramesInPageOrder(meta);
    const base = dirOf(htmlPath);
    // Le dossier nommé dans la page, à côté d'elle ; sinon tout dossier qui contient les vues attendues.
    const dirs = [params.folderName ? `${base}${params.folderName}/` : base, ...new Set(all.map(dirOf))];
    let frames: string[] | null = null;
    let missing = total;
    for (const d of dirs) {
      const found = order.map(({ u, v }) => byLower.get(`${d}${xrFrameName(u, v, params.imageExtension)}`.toLowerCase()) ?? null);
      const miss = found.filter((x) => x === null).length;
      if (miss === 0) { frames = found as string[]; break; }
      if (miss < missing) missing = miss;
    }
    if (!frames) {
      failure = missing === total && all.length <= 2
        ? lone360Message(htmlPath.split('/').pop() ?? htmlPath)
        : `Export KeyShotXR incomplet : ${missing} vue${missing > 1 ? 's' : ''} sur ${total} introuvable${missing > 1 ? 's' : ''} (${xrFrameName(0, 0, params.imageExtension)}…). Attendez la fin du rendu dans KeyShot, puis déposez de nouveau le dossier.`;
      continue;
    }
    // L'original : tout le dossier déposé qui contient la page ; sinon la page et le dossier des vues.
    const top = htmlPath.includes('/') ? `${htmlPath.split('/')[0]}/` : '';
    const framesDir = dirOf(frames[0]!);
    const members = top ? all.filter((p) => p.startsWith(top)) : all.filter((p) => p === htmlPath || p.startsWith(framesDir));
    const name = top ? top.slice(0, -1) : (htmlPath.split('/').pop() ?? params.folderName).replace(/\.(html?|360)$/i, '') || params.folderName || 'KeyShotXR';
    return { ok: true, xr: { params, htmlPath, frames, members, name } };
  }
  return failure ? { ok: false, reason: failure } : null;
}

// ── Visionneuse : position, glissement, inertie, chargement ──────────────────────────────────────────

/** Avance d'une position continue sur un axe : en boucle (`wrap`), ou bornée aux extrémités. */
export function xrMove(pos: number, delta: number, count: number, wrap: boolean): number {
  if (count <= 1) return 0;
  const next = pos + delta;
  return wrap ? ((next % count) + count) % count : Math.min(count - 1, Math.max(0, next));
}

/** Indice entier d'une position continue (arrondi, en boucle ou borné). */
export function xrIndex(pos: number, count: number, wrap: boolean): number {
  if (count <= 1) return 0;
  const i = Math.round(pos);
  return wrap ? ((i % count) + count) % count : Math.min(count - 1, Math.max(0, i));
}

/** Page affichée pour une position (u, v) continue. */
export function xrPageAt(m: XrMeta, u: number, v: number): number {
  return xrPage(m, xrIndex(u, m.uCount, m.uWrap), xrIndex(v, m.vCount, m.vWrap));
}

/**
 * Vues parcourues pour un glissement de `dx` × `dy` pixels sur une scène de `width` × `height` : la largeur de la
 * scène fait un tour complet (toutes les vues d'une rangée), la hauteur fait toutes les rangées. Le sens suit KeyShot.
 */
export function xrDrag(m: XrMeta, dx: number, dy: number, width: number, height: number): { du: number; dv: number } {
  const du = m.uCount > 1 ? (m.uDir * dx * m.uCount) / Math.max(1, width) : 0;
  const dv = m.vCount > 1 ? (m.vDir * dy * m.vCount) / Math.max(1, height * 0.9) : 0;
  return { du, dv };
}

/** Inertie légère : la vitesse (vues par milliseconde) décroît de 12 % toutes les 16,7 ms ; null quand elle s’éteint. */
export const XR_DAMPING = 0.88;
export const XR_MIN_SPEED = 0.0015;
export function xrInertia(speed: number, dtMs: number): number | null {
  const next = speed * Math.pow(XR_DAMPING, Math.max(0, dtMs) / (1000 / 60));
  return Math.abs(next) < XR_MIN_SPEED ? null : next;
}

/** Positions visitées sur un axe à partir du départ, des pas larges aux pas fins (8, 4, 2, 1…) : un tour grossier d'abord. */
function coarseToFine(start: number, count: number, wrap: boolean): number[] {
  const seen = new Set<number>([start]);
  const out = [start];
  // Premier pas : environ un quart de tour (8 vues sur 36), puis 4, 2, 1.
  let step = 1;
  while (step * 2 <= count / 4) step *= 2;
  for (; step >= 1; step = Math.floor(step / 2)) {
    for (let k = step; k < count + step; k += step) {
      for (const s of wrap ? [k] : [k, -k]) {
        const i = wrap ? (start + s) % count : start + s;
        if (i >= 0 && i < count && !seen.has(i)) { seen.add(i); out.push(i); }
      }
    }
    if (step === 1) break;
  }
  for (let i = 0; i < count; i++) if (!seen.has(i)) out.push(i);
  return out;
}

/**
 * Ordre de chargement des pages : la vue de départ, puis sa rangée des pas larges aux pas fins (la rotation devient
 * possible très tôt, de plus en plus fluide), puis les autres rangées, des plus proches aux plus lointaines.
 */
export function xrLoadOrder(m: XrMeta): number[] {
  const rows = Array.from({ length: m.vCount }, (_, v) => v).sort((a, b) => Math.abs(a - m.vStart) - Math.abs(b - m.vStart) || a - b);
  const across = coarseToFine(m.uStart, m.uCount, m.uWrap);
  const out: number[] = [];
  for (const v of rows) for (const u of across) out.push(xrPage(m, u, v));
  return out;
}

/**
 * Page à montrer quand la vue voulue n'est pas encore chargée : la plus proche sur la même rangée (de part et
 * d'autre), sinon la vue de départ si elle l'est ; null si rien n'est prêt.
 */
export function xrNearestLoaded(m: XrMeta, u: number, v: number, loaded: (page: number) => boolean): number | null {
  const ui = xrIndex(u, m.uCount, m.uWrap), vi = xrIndex(v, m.vCount, m.vWrap);
  for (let d = 0; d <= m.uCount; d++) {
    for (const s of d ? [d, -d] : [0]) {
      const uu = m.uWrap ? (((ui + s) % m.uCount) + m.uCount) % m.uCount : ui + s;
      if (uu < 0 || uu >= m.uCount) continue;
      const p = xrPage(m, uu, vi);
      if (loaded(p)) return p;
    }
  }
  return loaded(0) ? 0 : null;
}

/** « 72 vues (36 × 2) », « 36 vues » : texte de World. */
export function xrSummary(m: Pick<XrMeta, 'uCount' | 'vCount'>): string {
  const n = xrFrameCount(m);
  return `${n} vue${n > 1 ? 's' : ''}${m.vCount > 1 ? ` (${m.uCount} × ${m.vCount})` : ''}`;
}
